// src/components/lesson/LessonMedia.jsx

import React from "react";

export default function LessonMedia({
  type = "image",

  src = "",
  poster = "",

  alt = "",
  label = "",

  controls = true,

  autoPlay = false,
  loop = false,
  muted = false,

  className = "",
}) {
  const resolvedType =
    type === "video" ||
    type === "animation" ||
    type === "image"
      ? type
      : "image";

  /*
   * No media yet.
   *
   * Keep the correct Canva-sized media
   * area visible while we are testing the
   * frontend before adding Sanity fields.
   */
  if (!src) {
    return (
      <div
        className={[
          "flex aspect-video w-full items-center justify-center",
          "overflow-hidden rounded-[22px]",
          "bg-[#F1F1F1]",
          className,
        ].join(" ")}
      >
        <div className="px-6 text-center">
          <div
            className="
              mx-auto
              flex h-14 w-14
              items-center justify-center
              rounded-full
              bg-white
              shadow-sm
            "
            aria-hidden="true"
          >
            <span className="ml-1 text-xl text-[#38B6E8]">
              ▶
            </span>
          </div>

          <p className="mt-3 text-xs font-semibold text-[#9A9A9A]">
            Lesson media
          </p>
        </div>
      </div>
    );
  }

  /*
   * STANDARD LESSON VIDEO
   *
   * Intro videos use normal browser
   * controls including play/pause,
   * scrubber and fullscreen.
   */
  if (resolvedType === "video") {
    return (
      <div
        className={[
          "relative aspect-video w-full overflow-hidden",
          "rounded-[22px] bg-black",
          className,
        ].join(" ")}
      >
        <video
          src={src}
          poster={poster || undefined}
          controls={controls}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline
          preload="metadata"
          aria-label={
            label ||
            alt ||
            "Lesson video"
          }
          className="h-full w-full object-cover"
        >
          Your browser does not support
          video playback.
        </video>
      </div>
    );
  }

  /*
   * EDUCATIONAL ANIMATION
   *
   * MP4/WebM animations can autoplay,
   * stay muted and loop continuously.
   *
   * This is intentionally separate from
   * the main video mode because these
   * animations should feel like visual
   * explainers rather than video players.
   */
  if (resolvedType === "animation") {
    return (
      <div
        className={[
          "relative aspect-video w-full overflow-hidden",
          "rounded-[22px] bg-[#F5F5F5]",
          className,
        ].join(" ")}
      >
        <video
          src={src}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-label={
            label ||
            alt ||
            "Lesson animation"
          }
          className="h-full w-full object-contain"
        />
      </div>
    );
  }

  /*
   * STATIC IMAGE / GIF / WEBP
   */
  return (
    <div
      className={[
        "flex w-full items-center justify-center",
        "overflow-hidden rounded-[22px]",
        "bg-[#F5F5F5]",
        className,
      ].join(" ")}
    >
      <img
        src={src}
        alt={alt}
        className="h-auto max-h-[340px] w-full object-contain"
        loading="eager"
        decoding="async"
        draggable="false"
      />
    </div>
  );
}